/**
 * useMinistries Hook
 *
 * Fetches ministries using URQL query.
 */

import { useQuery } from "urql";
import { type Ministry } from "../types.ts";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const MinistriesDocument = `
  query Ministries {
    ministries {
      id
      code
      name
      description
      imageUrl
      isActive
      sortOrder
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseMinistriesOptions {
  pause?: boolean;
}

interface UseMinistriesResult {
  ministries: Ministry[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Fetch ministries list.
 */
export function useMinistries(
  options: UseMinistriesOptions = {}
): UseMinistriesResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ ministries: Ministry[] }>({
    query: MinistriesDocument,
    pause,
  });

  return {
    ministries: result.data?.ministries ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
